import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DriveArtifactFlowBoard } from './DriveArtifactFlowBoard';
import { PersonalLayerDriveControls } from './PersonalLayerDriveControls';
import { manifestDetailLevel } from './driveStateColors';
import {
  defaultDateRangeInputs,
  deriveDriveSummaryFromArtifacts,
  filterArtifactsByWindow,
  isFutureWindowEnd,
  periodOffFromReportingPeriodCode,
  reportingPeriodCodeForWindow,
  type DriveWindowSelection,
} from './driveWindow';
import type {
  DriveDetailLevel,
  DriveHiFiModel,
  DriveLaneMonitorStage,
  DriveWindowMode,
  PersonalOverlayMode,
} from './types';
import styles from '../ControlBridge.module.css';

export interface PersonalLayerDriveProxyProps {
  model: DriveHiFiModel;
  overlayMode: PersonalOverlayMode;
  scopeLabel?: string;
  resourceLabel?: string;
  onClearResource?: () => void;
  recommendedSurface?: import('../attentionRouting').RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  onOpenItemDetail?: (itemId: string) => void;
  /** Envelope owns the stage board — keep Viewing/Resource plus the period window. */
  contextOnly?: boolean;
  /** Notify parent when the Drive reporting-period code changes (YYYY-MM). */
  onDrivePeriodChange?: (periodCode: string) => void;
}

const DriveContextMeta: React.FC<{
  scopeLabel?: string;
  resourceLabel?: string;
  onClearResource?: () => void;
}> = ({ scopeLabel, resourceLabel, onClearResource }) => {
  if (!scopeLabel || !resourceLabel) return null;
  return (
    <div className={styles.driveContextMeta} aria-label="Active filters">
      <span>
        Viewing: <strong>{scopeLabel}</strong>
      </span>
      <span aria-hidden="true"> · </span>
      <span>
        Resource: <strong>{resourceLabel}</strong>
        {onClearResource && resourceLabel !== 'All resources' ? (
          <button type="button" className={styles.clearFilter} onClick={onClearResource}>
            Clear
          </button>
        ) : null}
      </span>
    </div>
  );
};

function useDriveWindowState(
  model: DriveHiFiModel,
  onDrivePeriodChange?: (periodCode: string) => void
) {
  const defaultRange = defaultDateRangeInputs();
  const engineCode =
    model.activeReportingPeriod?.code || model.activeReportingPeriod?.start || undefined;
  const initialOff = periodOffFromReportingPeriodCode(engineCode) ?? 0;
  const [win, setWin] = useState<DriveWindowMode>('period');
  const [periodDiv, setPeriodDiv] = useState<'month' | 'quarter' | 'year'>('month');
  const [periodOff, setPeriodOff] = useState(initialOff);
  const [dStart, setDStart] = useState(defaultRange.dStart);
  const [dEnd, setDEnd] = useState(defaultRange.dEnd);
  const seededCodeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const code =
      model.activeReportingPeriod?.code || model.activeReportingPeriod?.start || undefined;
    if (!code || code === seededCodeRef.current) return;
    const off = periodOffFromReportingPeriodCode(code);
    if (off === null) return;
    seededCodeRef.current = code;
    setPeriodOff(off);
    setWin('period');
    setPeriodDiv('month');
  }, [model.activeReportingPeriod?.code, model.activeReportingPeriod?.start]);

  const windowState: DriveWindowSelection = useMemo(
    () => ({ win, periodDiv, periodOff, dStart, dEnd }),
    [win, periodDiv, periodOff, dStart, dEnd]
  );

  useEffect(() => {
    if (win !== 'period' || !onDrivePeriodChange) return;
    onDrivePeriodChange(reportingPeriodCodeForWindow({ periodDiv, periodOff }));
  }, [win, periodDiv, periodOff, onDrivePeriodChange]);

  const futureWindowHint = isFutureWindowEnd(windowState)
    ? 'Future window selected — Projected on'
    : undefined;
  return {
    win,
    setWin,
    periodDiv,
    setPeriodDiv,
    periodOff,
    dStart,
    dEnd,
    windowState,
    futureWindowHint,
    onPeriodStep: (delta: number) => setPeriodOff((v) => v + delta),
    onDateRangeChange: (field: 'dStart' | 'dEnd', value: string) => {
      if (field === 'dStart') setDStart(value);
      else setDEnd(value);
    },
  };
}

const LaneMonitorStagesStrip: React.FC<{ stages?: DriveLaneMonitorStage[] }> = ({ stages }) => {
  if (!stages?.length) return null;
  const present = stages.filter((s) => s.layerAStepPresent).length;
  const demand = stages.find((s) => s.monitorId === 'demand_channel_inputs');
  const channelCount = demand?.demandChannels?.length ?? 0;
  const periodCounts = demand?.demandChannelPeriodCounters?.counts;
  const periodBits = periodCounts
    ? (['website', 'email', 'phone'] as const)
        .map((k) => {
          const n = periodCounts[k];
          return typeof n === 'number' ? `${k} ${n}` : '';
        })
        .filter(Boolean)
        .join(' / ')
    : '';
  return (
    <div
      className={styles.laneMonitorStrip}
      data-testid="drive-lane-monitor-stages"
      aria-label="Business lane monitor stages"
    >
      <span className={styles.laneMonitorStripMeta}>
        Monitors {present}/{stages.length}
        {channelCount ? ` · channels ${channelCount}` : ''}
        {periodBits ? ` · period ${periodBits}` : ''}
      </span>
      <ul className={styles.laneMonitorStripList}>
        {stages.map((stage) => (
          <li
            key={stage.monitorId}
            className={
              stage.layerAStepPresent
                ? styles.laneMonitorChipPresent
                : styles.laneMonitorChipMissing
            }
            title={`${stage.label} (${stage.laneKey || 'lane'})`}
          >
            {stage.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DriveEnvelopeContextBar: React.FC<PersonalLayerDriveProxyProps> = ({
  model,
  scopeLabel,
  resourceLabel,
  onClearResource,
  onDrivePeriodChange,
}) => {
  const {
    win,
    setWin,
    periodDiv,
    setPeriodDiv,
    periodOff,
    dStart,
    dEnd,
    futureWindowHint,
    onPeriodStep,
    onDateRangeChange,
  } = useDriveWindowState(model, onDrivePeriodChange);

  return (
    <div className={styles.personalLayerPanel} data-testid="personal-layer-drive-proxy">
      <div className={styles.driveContextBar}>
        <DriveContextMeta
          scopeLabel={scopeLabel}
          resourceLabel={resourceLabel}
          onClearResource={onClearResource}
        />
        <PersonalLayerDriveControls
          windowOnly
          artifactFlowMode
          variant="inline"
          win={win}
          periodDiv={periodDiv}
          periodOff={periodOff}
          dStart={dStart}
          dEnd={dEnd}
          futureWindowHint={futureWindowHint}
          onWinChange={setWin}
          onPeriodDivChange={setPeriodDiv}
          onPeriodStep={onPeriodStep}
          onDateRangeChange={onDateRangeChange}
        />
      </div>
    </div>
  );
};

const PersonalLayerDriveBoard: React.FC<PersonalLayerDriveProxyProps> = ({
  model,
  scopeLabel,
  resourceLabel,
  onClearResource,
  recommendedSurface,
  onNavigate,
  onOpenArtifactDetail,
  onOpenItemDetail,
  onDrivePeriodChange,
}) => {
  const defaultLod = manifestDetailLevel(model.filterHints?.defaultDetailLevel);
  const [lod, setLod] = useState<DriveDetailLevel>(defaultLod);
  const [projected, setProjected] = useState(false);
  const {
    win,
    setWin,
    periodDiv,
    setPeriodDiv,
    periodOff,
    dStart,
    dEnd,
    windowState,
    futureWindowHint,
    onPeriodStep,
    onDateRangeChange,
  } = useDriveWindowState(model, onDrivePeriodChange);

  useEffect(() => {
    setLod(manifestDetailLevel(model.filterHints?.defaultDetailLevel));
  }, [model.filterHints?.defaultDetailLevel, model.domainLabel]);

  useEffect(() => {
    if (isFutureWindowEnd(windowState)) {
      setProjected(true);
    }
  }, [windowState]);

  const windowArtifacts = useMemo(
    () => filterArtifactsByWindow(model.artifacts, windowState),
    [model.artifacts, windowState]
  );
  const summary = useMemo(
    () => deriveDriveSummaryFromArtifacts(windowArtifacts),
    [windowArtifacts]
  );

  const flowModel = useMemo(
    () => ({ ...model, artifacts: windowArtifacts, summary }),
    [model, windowArtifacts, summary]
  );

  if (model.hasOperationalSession === false) {
    return (
      <div className={styles.personalLayerPanel} data-testid="personal-layer-drive-proxy">
        <div className={styles.driveContextBar}>
          <DriveContextMeta
            scopeLabel={scopeLabel}
            resourceLabel={resourceLabel}
            onClearResource={onClearResource}
          />
        </div>
        <p role="status" data-testid="drive-no-ops-session">
          Drive needs an Operational bridge session. Run{' '}
          <code>kryspace ops-wbs ensure</code> (or Ops WBS activate) for this company, then reload.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.personalLayerPanel} data-testid="personal-layer-drive-proxy">
      <div className={styles.driveContextBar}>
        <DriveContextMeta
          scopeLabel={scopeLabel}
          resourceLabel={resourceLabel}
          onClearResource={onClearResource}
        />
        <PersonalLayerDriveControls
          lod={lod}
          projected={projected}
          win={win}
          periodDiv={periodDiv}
          periodOff={periodOff}
          dStart={dStart}
          dEnd={dEnd}
          futureWindowHint={futureWindowHint}
          artifactFlowMode
          variant="inline"
          onLodChange={setLod}
          onProjectedChange={setProjected}
          onWinChange={setWin}
          onPeriodDivChange={setPeriodDiv}
          onPeriodStep={onPeriodStep}
          onDateRangeChange={onDateRangeChange}
        />
      </div>

      <div className={styles.personalLayerSummaryHiFi} aria-label="Drive summary">
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Moving</span>
          <span className={styles.personalLayerSummaryValue}>{summary.inFlow}</span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Moved</span>
          <span className={styles.personalLayerSummaryValue}>{summary.movedRecently}</span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Held</span>
          <span className={styles.personalLayerSummaryValue}>{summary.blocked}</span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Expected</span>
          <span className={styles.personalLayerSummaryValue}>{summary.expectedToMove}</span>
        </div>
      </div>

      <LaneMonitorStagesStrip stages={model.laneMonitorStages} />

      <DriveArtifactFlowBoard
        model={flowModel}
        lod={lod}
        recommendedSurface={recommendedSurface}
        onNavigate={onNavigate}
        onOpenArtifactDetail={onOpenArtifactDetail}
        onOpenItemDetail={onOpenItemDetail}
      />
    </div>
  );
};

export const PersonalLayerDriveProxy: React.FC<PersonalLayerDriveProxyProps> = (props) => {
  if (props.contextOnly) {
    return <DriveEnvelopeContextBar {...props} />;
  }
  return <PersonalLayerDriveBoard {...props} />;
};
